import { apiRequest } from '../api/client';
import { fetchBlob, downloadBlob } from '../api/binary';

// `GET /members/export`/`GET /payments/export` (HAMMER 1D E5) answer a file,
// not JSON — `fetchBlob`/`downloadBlob` (E6's `api/binary.js`, reused here as
// the plan's Global Constraints ask) fetch it with the session cookie and
// trigger the browser's "Save as" via a throwaway `<a download>`. The server
// names the file itself (`Content-Disposition`); `fallbackFilename` only
// covers the (defensive, shouldn't happen) case where that header is absent.
async function downloadExport(path, fallbackFilename) {
  const { blob, filename } = await fetchBlob(path);
  downloadBlob(blob, filename || fallbackFilename);
}

// Builds a query string skipping undefined/null/empty values, same
// convention as `training-api.js#buildQuery`.
function toQuery(params) {
  const search = new URLSearchParams();
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Admin-only reads/writes, kept as a plain object (like auth-api.js) so
 * tests can inject a fake implementation without mocking fetch.
 *
 * `listPlans`/`createPlan`/`updatePlan` back `/admin/planes` (Task 11, spec
 * "Planes"): `GET /plans` returns the whole catalog to an admin (active and
 * inactive); `POST`/`PATCH /plans/:id` accept `price` as the exact string the
 * form carries — this module never parses it into a number, since the
 * server is the only validator of the amount (spec: "el cliente sólo
 * presenta DTOs"). Task 9's member/period form reuses `listPlans` instead of
 * duplicating a second plans loader.
 *
 * `listMembers`/`createMember` back `/admin/socios` (Task 8, spec
 * "Administrador": "Socios consume /members"). `createMember` forwards
 * exactly the whitelist the form already built (username, email, password,
 * fitnessGoal, targetDaysPerWeek) — this module adds nothing and strips
 * nothing, `POST /members` is the one place that enforces the whitelist.
 *
 * `getMember`/`updateMember`/`createMembership`/`resetMemberPassword` back
 * `/admin/socios/:id` (Task 9, spec "Administrador": "el detalle consume
 * /members/:id"). `updateMember` forwards whatever whitelist keys the caller
 * built (profile edit sends username/email/fitnessGoal/targetDaysPerWeek;
 * activate/deactivate sends only `isActive`) — same "this module adds/strips
 * nothing" rule as `createMember`. `createMembership` forwards the period
 * input as-is; the caller (`MembershipForm`) only includes `endDate` when the
 * admin typed one, since the server computes a month when it's omitted.
 * `resetMemberPassword` sends only `{ temporaryPassword }` — the target id
 * already lives in the URL, never in the body.
 *
 * `recordPayment` backs Task 10's cobro flow: forwards the exact five-key
 * signature (`membershipId`, `idempotencyKey`, `amount`, `date`, `method`)
 * `PaymentForm` built — this module adds/strips nothing, same rule as every
 * other write here. `PaymentForm` (via `createPendingPaymentStore`) is the
 * one responsible for persisting that signature+UUID BEFORE this call and
 * only clearing it on a definitive 200/201, so a lost response never leaves
 * the admin able to fire a second, different charge for the same period.
 */
export const adminApi = {
  getDashboard: () => apiRequest('/dashboard'),
  getSettings: () => apiRequest('/settings'),
  updateSettings: (settings) => apiRequest('/settings', { method: 'PUT', body: settings }),
  listCoaches: () => apiRequest('/coaches'),
  createCoach: (data) => apiRequest('/coaches', { method: 'POST', body: data }),
  updateCoach: (id, changes) => apiRequest(`/coaches/${encodeURIComponent(id)}`, { method: 'PATCH', body: changes }),
  resetCoachPassword: (id, temporaryPassword) => apiRequest(`/coaches/${encodeURIComponent(id)}/reset-password`, {
    method: 'POST', body: { temporaryPassword },
  }),
  listKiosks: () => apiRequest('/kiosks'),
  // Ingresos de un día (`{ date }` opcional; sin fecha, hoy en hora argentina).
  listCheckins: (params) => apiRequest(`/attendance${toQuery(params)}`),
  createKiosk: (label) => apiRequest('/kiosks', { method: 'POST', body: { label } }),
  updateKiosk: (id, changes) => apiRequest(`/kiosks/${encodeURIComponent(id)}`, { method: 'PATCH', body: changes }),
  rotateKioskCredentials: (id) => apiRequest(`/kiosks/${encodeURIComponent(id)}/rotate-credentials`, { method: 'POST' }),
  listPlans: () => apiRequest('/plans'),
  createPlan: (data) => apiRequest('/plans', { method: 'POST', body: data }),
  updatePlan: (id, data) => apiRequest(`/plans/${id}`, { method: 'PATCH', body: data }),
  listMembers: () => apiRequest('/members'),
  createMember: (data) => apiRequest('/members', { method: 'POST', body: data }),
  getMember: (id) => apiRequest(`/members/${encodeURIComponent(id)}`),
  updateMember: (id, changes) => apiRequest(`/members/${encodeURIComponent(id)}`, { method: 'PATCH', body: changes }),
  createMembership: (input) => apiRequest('/memberships', { method: 'POST', body: input }),
  // Períodos (spec 2026-10-08): editar, eliminar (sin pagos) y anular (con motivo).
  updateMembership: (id, changes) => apiRequest(`/memberships/${encodeURIComponent(id)}`, { method: 'PATCH', body: changes }),
  deleteMembership: (id) => apiRequest(`/memberships/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  voidMembership: (id, reason) => apiRequest(`/memberships/${encodeURIComponent(id)}/void`, { method: 'POST', body: { reason } }),
  resetMemberPassword: (id, temporaryPassword, useDocumentAsPassword = false) => apiRequest(`/members/${encodeURIComponent(id)}/reset-password`, {
    method: 'POST',
    body: useDocumentAsPassword ? { useDocumentAsPassword: true } : { temporaryPassword },
  }),
  setMemberPasswordsToDocument: () => apiRequest('/members/passwords/use-document', { method: 'POST', body: {} }),
  recordPayment: (input) => apiRequest('/payments', { method: 'POST', body: input }),

  // `exportMembers`/`exportPayments`/`listPayments` back HAMMER 1D E5
  // ("Socios": botones Exportar Excel/CSV; "Pagos": listado + export). Every
  // param the caller builds (from/to/method/memberId/format) is forwarded
  // as-is via `toQuery` — same "this module adds/strips nothing" rule as the
  // JSON writes above — the server is the sole validator of each filter.
  exportMembers: (format) => downloadExport(`/members/export${toQuery({ format })}`, `socios-hammer.${format || 'xlsx'}`),
  listPayments: (params) => apiRequest(`/payments${toQuery(params)}`),
  exportPayments: (params) => downloadExport(`/payments/export${toQuery(params)}`, `pagos-hammer.${(params && params.format) || 'xlsx'}`),
  getOperationsReport: (params) => apiRequest(`/reports/operations${toQuery(params)}`),
  downloadOperationsReport: async (params = {}) => {
    const format = params.format || 'xlsx';
    const { blob, filename } = await fetchBlob(`/reports/operations/export${toQuery(params)}`);
    downloadBlob(blob, filename || `reportes-operativos.${format}`);
  },
};
